import React, { useState } from "react";
import styled, { css } from "styled-components";
import { Button, Col, Label, Row, MenuItem, DeleteMenuItem } from "components/bootstrap";
import { IconButton } from "components/common";
import { MORE_ACTIONS_TITLE, MORE_ACTIONS_HOVER_TITLE } from "components/common/EntityDataTable/Constants";
import OverlayDropdownButton from "components/common/OverlayDropdownButton";
import { RuleBuilderTypes } from "./types";
import { useRuleBuilder } from "./RuleBuilderContext";
const Highlighted = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.variant.info};
    font-weight: bold;
  `
);
const TypeLabel = styled(Label)(
  ({ theme }) => css`
    margin-left: ${theme.spacings.xs};
  `
);
const StyledRow = styled(Row)(
  ({ theme, $hovered }) => css`
    cursor: pointer;
    display: flex;
    align-items: center;
    margin: 0;
    height: ${theme.spacings.xl};
    background-color: ${$hovered ? theme.colors.table.row.backgroundHover : "transparent"};
    border-left: solid 1px ${theme.colors.gray[80]};
  `
);
const NegationButton = styled(Button)(
  ({ theme, $negate }) => css`
    opacity: ${$negate ? "1" : "0.3"};
    margin-right: ${theme.spacings.sm};
  `
);
const BlockTitle = styled.h5`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;
const ErrorMessage = styled.p(
  ({ theme }) => css`
    color: ${theme.colors.variant.danger};
    font-size: 0.75rem;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    margin: 0;
  `
);
const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
`;
const EditIconButton = styled(IconButton)(
  ({ theme }) => css`
    margin-right: ${theme.spacings.xs};
  `
);
const DeprecatedLabel = styled(Label)`
  padding: 2px 8px;
`;
const RuleBlockDisplay = ({
  block = void 0,
  negatable = false,
  onEdit,
  onDelete,
  onNegate,
  onDuplicate,
  onInsertAbove,
  onInsertBelow,
  selectedBlockDict = void 0,
  type
}) => {
  var _a, _b;
  const [showActions, setShowActions] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedOutput, setHighlightedOutput] = useRuleBuilder().useHighlightedOutput;
  const handleDropdownToggle = () => {
    setIsDropdownOpen((prev) => !prev);
    setShowActions(!isDropdownOpen);
  };
  const readableReturnType = (_type) => {
    switch (_type) {
      case RuleBuilderTypes.Number:
        return "Number";
      default:
        return _type == null ? void 0 : _type.slice(((_type == null ? void 0 : _type.lastIndexOf(".")) || 0) + 1);
    }
  };
  const returnTypeLabel = readableReturnType(selectedBlockDict == null ? void 0 : selectedBlockDict.return_type);
  const highlightedRuleTitle = (termToHighlight, title = "") => {
    const parts = title.split(/('\$.*?')/);
    const partsWithHighlight = parts.map((part) => {
      if (part === `'$${termToHighlight}'`) {
        return /* @__PURE__ */ React.createElement(Highlighted, { key: part }, part);
      }
      return part;
    });
    return partsWithHighlight.map((item, index) => (
      // eslint-disable-next-line react/no-array-index-key
      /* @__PURE__ */ React.createElement(React.Fragment, { key: index }, item)
    ));
  };
  const errorMessage = (_a = block == null ? void 0 : block.errors) == null ? void 0 : _a.join(", ");
  return /* @__PURE__ */ React.createElement(
    StyledRow,
    {
      onMouseEnter: () => setShowActions(true),
      onMouseLeave: !isDropdownOpen ? () => setShowActions(false) : void 0,
      $hovered: showActions
    },
    /* @__PURE__ */ React.createElement(Col, { xs: 9, md: 10 }, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { xs: 10, md: 9 }, /* @__PURE__ */ React.createElement(BlockTitle, { title: block == null ? void 0 : block.step_title }, negatable && /* @__PURE__ */ React.createElement(
      NegationButton,
      {
        bsStyle: "primary",
        bsSize: "xs",
        $negate: block == null ? void 0 : block.negate,
        onClick: (e) => {
          e.target.blur();
          onNegate();
        }
      },
      "Not"
    ), highlightedOutput ? highlightedRuleTitle(highlightedOutput, block == null ? void 0 : block.step_title) : block == null ? void 0 : block.step_title, (selectedBlockDict == null ? void 0 : selectedBlockDict.deprecated) && /* @__PURE__ */ React.createElement("span", null, "\xA0", /* @__PURE__ */ React.createElement(DeprecatedLabel, { bsStyle: "warning", bsSize: "xs" }, "Deprecated")), ((_b = block == null ? void 0 : block.errors) == null ? void 0 : _b.length) > 0 && /* @__PURE__ */ React.createElement(ErrorMessage, { title: errorMessage }, errorMessage))), (block == null ? void 0 : block.outputvariable) && /* @__PURE__ */ React.createElement(Col, { xs: 2, md: 3 }, /* @__PURE__ */ React.createElement(
      Label,
      {
        bsStyle: "primary",
        onMouseEnter: () => setHighlightedOutput(block.outputvariable),
        onMouseLeave: () => setHighlightedOutput(void 0)
      },
      `$${block.outputvariable}`
    ), returnTypeLabel && /* @__PURE__ */ React.createElement(TypeLabel, { bsStyle: "default" }, returnTypeLabel)))),
    /* @__PURE__ */ React.createElement(Col, { xs: 3, md: 2, className: "text-right" }, showActions && type === "condition" && /* @__PURE__ */ React.createElement(ActionsContainer, null, /* @__PURE__ */ React.createElement(IconButton, { name: "edit_square", onClick: onEdit, title: "Edit" }), /* @__PURE__ */ React.createElement(IconButton, { name: "delete", onClick: onDelete, title: "Delete" })), showActions && type === "action" && /* @__PURE__ */ React.createElement(ActionsContainer, null, /* @__PURE__ */ React.createElement(EditIconButton, { name: "edit_square", onClick: onEdit, title: "Edit" }), /* @__PURE__ */ React.createElement(
      OverlayDropdownButton,
      {
        title: MORE_ACTIONS_TITLE,
        buttonTitle: MORE_ACTIONS_HOVER_TITLE,
        bsSize: "xsmall",
        onToggle: handleDropdownToggle,
        dropdownZIndex: 1500
      },
      /* @__PURE__ */ React.createElement(MenuItem, { onClick: onEdit }, "Edit"),
      /* @__PURE__ */ React.createElement(
        MenuItem,
        {
          onClick: () => {
            onDuplicate();
            handleDropdownToggle();
          }
        },
        "Duplicate"
      ),
      /* @__PURE__ */ React.createElement(MenuItem, { divider: true }),
      /* @__PURE__ */ React.createElement(MenuItem, { onClick: onInsertAbove }, "Insert above"),
      /* @__PURE__ */ React.createElement(MenuItem, { onClick: onInsertBelow }, "Insert below"),
      /* @__PURE__ */ React.createElement(MenuItem, { divider: true }),
      /* @__PURE__ */ React.createElement(DeleteMenuItem, { onClick: onDelete })
    )))
  );
};
export default RuleBlockDisplay;
